import React, { useEffect, useState } from "react";
import { useProfesional } from "../context/ProfesionalContext";
import { safeFetch } from "@/api/safeFetch";
import ImageCarouselLanding from "../util/ImageCarouselLanding";
import { CircleHelp, ChevronDown, ChevronUp } from "lucide-react";

type TeamMember = {
  nombre: string;
  cargo: string;
  foto: string;
};

const QuienesSomosLanding: React.FC = () => {
  const [qs, setQs] = useState<any>(null);

  const { perfilId, landingData } = useProfesional();

  const [openFaq, setOpenFaq] = useState<number | null>(null);


  const styles = landingData?.theme?.globalStyles;

  useEffect(() => {
    if (!perfilId) return;

    const load = async () => {
      try {
        const res = await safeFetch(`/api/landing/${perfilId}/who-we-are`);

        const json = await res.json();

        if (json?.ok) {
          setQs(json.data);
        }
      } catch (err) {
        console.error(err);
      }
    };

    load();
  }, [perfilId]);

  const title = qs?.title || "Quiénes somos";

  const description =
    qs?.description ||
    "Somos un equipo de profesionales comprometidos con ofrecer soluciones de calidad adaptadas a cada cliente.";

  const mission =
    qs?.mission || "Ofrecer servicios profesionales de alta calidad.";

  const vision = qs?.vision || "Ser una empresa referente en el sector.";

  const values: string[] = qs?.values ?? [
    "Profesionalidad",
    "Transparencia",
    "Compromiso",
    "Innovación",
  ];

  const extraTitle = qs?.extraTitle || "¿Por qué trabajar con nosotros?";

  const extraDescription =
    qs?.extraDescription ||
    "Acompañamos a nuestros clientes en cada etapa del proceso.";

  const extraPoints: string[] = qs?.extraPoints || [
    "Años de experiencia en el sector.",
    "Equipo multidisciplinar especializado.",
    "Metodología clara y transparente.",
    "Acompañamiento continuo.",
  ];


  const faq: { question: string; answer: string }[] = qs?.faq || [
    {
      question: "¿Con qué tipo de clientes trabajáis?",
      answer: "Con empresas de todos los tamaños.",
    },
    {
      question: "¿Cómo empezamos a trabajar juntos?",
      answer: "Con una reunión inicial para entender tus necesidades.",
    },
  ];

  const sliderImages: string[] = qs?.sliderImages || ["/icons/cuadranex.png"];

  const team: TeamMember[] = qs?.team ?? [
    {
      nombre: "Nombre del profesional",
      cargo: "Cargo o puesto",
      foto: "https://cdn-icons-png.flaticon.com/512/3177/3177440.png",
    },
  ];



  return (
    <div className="w-full max-w-6xl mx-auto px-6 flex flex-col gap-20 pb-10">
      {/* INTRO */}
      <section className="text-center space-y-6">
        <h1
          className="font-extrabold tracking-tight text-4xl"
          style={{
            color: styles?.h1Color,
            fontFamily: styles?.fontFamily,
          }}
        >
          {title}
        </h1>

        <p
          className="max-w-3xl mx-auto leading-relaxed"
          style={{
            color: styles?.textColor,
            fontFamily: styles?.fontFamily,
          }}
        >
          {description}
        </p>
      </section>

      {/* CARDS */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-8 ">
        {/* MISIÓN */}
        <div className="group bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8 shadow-xl transition-all hover:-translate-y-1 hover:shadow-2xl">
          <h2
            className="font-semibold mb-4"
            style={{
              color: styles?.h2Color,
              fontFamily: styles?.fontFamily,
            }}
          >
            🎯 Nuestra Misión
          </h2>

          <p
            style={{
              color: styles?.textColor,
              fontFamily: styles?.fontFamily,
            }}
          >
            {mission}
          </p>
        </div>

        {/* VISIÓN */}
        <div className="group bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8 shadow-xl transition-all hover:-translate-y-1 hover:shadow-2xl">
          <h2
            className="font-semibold mb-4"
            style={{
              color: styles?.h2Color,
              fontFamily: styles?.fontFamily,
            }}
          >
            🌟 Nuestra Visión
          </h2>

          <p
            style={{
              color: styles?.textColor,
              fontFamily: styles?.fontFamily,
            }}
          >
            {vision}
          </p>
        </div>

        {/* VALORES */}
        <div className="group bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8 shadow-xl transition-all hover:-translate-y-1 hover:shadow-2xl">
          <h2
            className="font-semibold mb-4"
            style={{
              color: styles?.h2Color,
              fontFamily: styles?.fontFamily,
            }}
          >
            💎 Nuestros Valores
          </h2>

          <ul className=" text-sm space-y-2">
            {values.map((v, i) => (
              <li key={i}>✔ {v}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* EXTRA + FAQ */}
      <section className="grid grid-cols-1 lg:grid-cols-1 gap-12 items-start">
        {/* EXTRA */}
        <div className="bg-white/5 border border-white/15 rounded-3xl p-8 h-full">
          <h2
            className="mb-5 font-semibold"
            style={{
              color: styles?.h2Color,
              fontFamily: styles?.fontFamily,
            }}
          >
            {extraTitle}
          </h2>

          <p
            className="mb-6 leading-relaxed"
            style={{
              color: styles?.h3Color,
              fontFamily: styles?.fontFamily,
            }}
          >
            {extraDescription}
          </p>

          <ul className="space-y-4">
            {extraPoints.map((p, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="text-cyan-400 mt-1 text-lg">✔</span>

                <p
                  className="leading-relaxed"
                  style={{
                    color: styles?.textColor,
                    fontFamily: styles?.fontFamily,
                  }}
                >
                  {p}
                </p>
              </li>
            ))}
          </ul>
        </div>

        {/* FAQ */}
       <div className="space-y-4">
  {faq.map((item, i) => {
    const opened = openFaq === i;

    return (
      <div
        key={i}
        className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden transition-all"
      >
        <button
          type="button"
          onClick={() => setOpenFaq(opened ? null : i)}
          className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-white/5 transition"
        >
          <div className="flex items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-cyan-500/15 border border-cyan-400/30">
              <CircleHelp className="h-5 w-5 text-cyan-400" />
            </div>

            <h3
              style={{
                color: styles?.h3Color,
                fontFamily: styles?.fontFamily,
              }}
            >
              {item.question}
            </h3>
          </div>

          {opened ? (
            <ChevronUp className="w-5 h-5 text-cyan-400 shrink-0" />
          ) : (
            <ChevronDown className="w-5 h-5 text-cyan-400 shrink-0" />
          )}
        </button>

        <div
          className={`transition-all duration-300 overflow-hidden ${
            opened ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="px-5 pb-5 pl-[76px]">
            <p
              className="leading-relaxed border-l-2 border-cyan-500/30 pl-4"
              style={{
                color: styles?.textColor,
                fontFamily: styles?.fontFamily,
              }}
            >
              {item.answer}
            </p>
          </div>
        </div>
      </div>
    );
  })}
</div>
      </section>

      {/* GALERÍA */}
      <section className="w-full flex flex-col gap-6">
        <h2
          className="font-semibold mb-4"
          style={{
            color: styles?.h2Color,
            fontFamily: styles?.fontFamily,
          }}
        >
          Galería
        </h2>

        <ImageCarouselLanding images={sliderImages} />
      </section>

      {/* EQUIPO */}
      <section className="w-full flex flex-col gap-10">
        <h2
          className="font-semibold mb-4"
          style={{
            color: styles?.h2Color,
            fontFamily: styles?.fontFamily,
          }}
        >
          Nuestro Equipo
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
          {team.map((persona, i) => (
            <div
              key={i}
              className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-6 flex flex-col items-center text-center shadow-xl hover:-translate-y-1 transition-all"
            >
              <div className="w-28 h-28 rounded-full overflow-hidden border border-white/20 mb-4">
                <img
                  src={persona.foto}
                  className="w-full h-full object-cover"
                />
              </div>

              <h3
                style={{
                  color: styles?.h3Color,
                  fontFamily: styles?.fontFamily,
                }}
              >
                {persona.nombre}
              </h3>

              <p
                style={{
                  color: styles?.textColor,
                  fontFamily: styles?.fontFamily,
                }}
              >
                {persona.cargo}
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default QuienesSomosLanding;
